import {Component, input, output} from '@angular/core';
import {DragDropModule, CdkDragEnd} from '@angular/cdk/drag-drop';
import {CommonModule} from '@angular/common';
import {ResizableDirective, ResizeEndEvent} from '../../../core/directives/resizable.directive';
import {clamp, pxToMm} from '../../../core/helpers/image.helper';

export enum WebSafeFont {
  Arial = 'Arial',
  TimesNewRoman = 'Times New Roman',
  CourierNew = 'Courier New',
  Georgia = 'Georgia',
  Verdana = 'Verdana',
  TrebuchetMS = 'Trebuchet MS',
  Tahoma = 'Tahoma',
  LucidaConsole = 'Lucida Console',
}

export type LabelElementType = 'text' | 'image';

export interface LabelConfig {
  widthMm: number;
  heightMm: number;
  backgroundColor: string;      // fallback
  backgroundImageUrl?: string;  // suporta transparência naturalmente
  backgroundSize?: 'cover' | 'contain' | '100% 100%';
  backgroundRepeat?: 'no-repeat' | 'repeat';
  backgroundObjectFit?: 'contain' | 'cover' | 'fill';
}

export interface BaseElement {
  id: string;
  name?: string;
  type: LabelElementType;
  xMm: number;
  yMm: number;
  wMm: number;
  hMm: number;
  zIndex: number;
}

export interface TextElement extends BaseElement {
  type: 'text';
  text: string;
  color: string;
  fontFamily: WebSafeFont;
  fontSizePt: number; // print-friendly
  textAlign: 'left' | 'right' | 'center' | 'justify';
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline';
  /** Espaçamento entre letras em px. 0 ou undefined = normal. */
  letterSpacingPx?: number;
  /** Espaçamento entre linhas (unitless). Ex: 1.2, 1.5. undefined = normal. */
  lineHeight?: number;
}

export interface ImageElement extends BaseElement {
  type: 'image';
  src: string; // dataURL ou URL
  objectFit: 'contain' | 'cover' | 'fill';
  opacity?: number;
}

export type LabelElement = TextElement | ImageElement;

@Component({
  selector: 'app-tag-canvas',
  imports: [
    CommonModule, DragDropModule, ResizableDirective
  ],
  standalone: true,
  templateUrl: './tagging-label-canvas.component.html',
  styleUrl: './tagging-label-canvas.component.scss'
})
export class TaggingLabelCanvasComponent {

  label = input.required<LabelConfig>();
  elements = input.required<LabelElement[]>();
  selectedId = input<string | null>(null);
  showCenterGuide = input<boolean>(false);
  /** Valores para substituir placeholders {{nome}}. null/undefined mantém o placeholder. */
  placeholderValues = input<Record<string, string | number | null | undefined>>({});

  updateElement = output<Partial<LabelElement> & { id: string }>();
  select = output<string>();

  /** letter-spacing: normal ou Xpx */
  getLetterSpacing(el: TextElement): string {
    const v = el.letterSpacingPx;
    if (v == null || v === 0) return 'normal';
    return `${v}px`;
  }

  /** line-height: normal ou unitless number */
  getLineHeight(el: TextElement): string {
    const v = el.lineHeight;
    if (v == null || v <= 0) return 'normal';
    return String(v);
  }

  /** Substitui {{placeholder}} pelo valor. null/undefined mantém o placeholder; '' ou 0 exibem normalmente. */
  resolvePlaceholders(text: string): string {
    const values = this.placeholderValues();
    if (!text || Object.keys(values).length === 0) return text ?? '';
    return text.replace(/\{\{(\w+)\}\}/g, (_, key) => {
      const val = values[key];
      if (val === null || val === undefined) return `{{${key}}}`;
      return String(val);
    });
  }

  onDragEnd(ev: CdkDragEnd, id: string) {
    const {x, y} = ev.distance;
    const dxMm = pxToMm(x);
    const dyMm = pxToMm(y);

    const el = this.elements().find(e => e.id === id);
    if (!el) return;

    const nextX = clamp(el.xMm + dxMm, 0, this.label().widthMm - el.wMm);
    const nextY = clamp(el.yMm + dyMm, 0, this.label().heightMm - el.hMm);

    ev.source.reset();

    this.updateElement.emit({id, xMm: nextX, yMm: nextY} as any);
  }

  onResizeEnd(ev: ResizeEndEvent, id: string) {
    this.updateElement.emit({id, ...ev} as any);
  }
}
